import LoadingButton from '@/common/components/base/button/loadingButton';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import usePersonalInquiryStore from '@/common/store/personalInquiry/usePersonalInquiry';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { ADJUSTER_REPORT_CULPRIT_PATHS } from '@/modules/adjusterReportModules/adjusterReport/links/culprit/culpritLinks';
import PersonalInquiry from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/PersonalInquiry';
import InitialLegalOwnerForm from '@/modules/adjusterReportModules/IncidentParticipants/components/culprit/owner/InitialLegalOwnerForm/InitialLegalOwnerForm';
import InitialRealOwnerForm from '@/modules/adjusterReportModules/IncidentParticipants/components/culprit/owner/InitialRealOwnerForm/InitialRealOwnerForm';
import {
  IOwnerInformationForm,
  IOwnerPersonalInformationForm,
} from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/getOwnerInformation/getOwnerInformationQuery.type';
import { usePatchOwnerInitialInfoQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/culprit/patchOwnerInitialInfo/patchOwnerInitialInfoQueryOptions';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

const InitialOwnerForm = () => {
  const methods = useFormContext<IOwnerInformationForm>();
  const { data: inquiryData } = usePersonalInquiryStore();
  const ownerType = useWatch({
    control: methods.control,
    name: 'personal_information.owner_type',
  });
  const { mutate, isPending: loading } = usePatchOwnerInitialInfoQueryOptions();
  const navigate = useNavigate();
  const { id } = useParams();

  const onSubmit = (formData: IOwnerInformationForm) => {
    let formObject = {} as IOwnerPersonalInformationForm;

    for (const key in formData.personal_information) {
      const typedKey = key as keyof IOwnerPersonalInformationForm;
      if (formData.personal_information[typedKey] === '') {
        formObject[typedKey] = null;
      } else formObject[typedKey] = formData?.personal_information[typedKey];
    }
    if (formData.personal_information?.owner_type === 'real') {
      formObject = {
        ...formObject,
        birthdate:
          formData.personal_information.birthdate &&
          convertPersianToGregorian(
            formData.personal_information.birthdate.toString(),
          ),
        name: null,
        registration_number: null,
        economic_code: null,
        national_id: null,
        owner_type: formData.personal_information.owner_type,
      };
    } else {
      formObject = {
        ...formObject,
        gender: null,
        first_name: null,
        last_name: null,
        birthdate: null,
        father_name: null,
        national_code: null,
        identity_number: null,
        identity_issue_location: null,
        owner_type: formData.personal_information?.owner_type || null,
      };
    }
    mutate(
      {
        data: formObject,
        id: id!,
      },
      {
        onSuccess: () => {
          navigate(
            ADJUSTER_REPORT_CULPRIT_PATHS.INFORMATION.DRIVER_OWNER.EDIT
              .ownerContact,
            {
              replace: true,
            },
          );
        },
      },
    );
  };

  const formatBirthDate = (dateStr?: string) => {
    if (!dateStr) return '';
    let year = dateStr.substring(0, 4);
    let month = dateStr.substring(4, 6);
    let day = dateStr.substring(6, 8);
    return `${year}/${month}/${day}`;
  };

  useEffect(() => {
    if (inquiryData?.national_code) {
      methods.reset({
        personal_information: {
          ...methods?.getValues(),
          birthdate: inquiryData?.birthdate
            ? formatBirthDate(inquiryData?.birthdate)
            : undefined,
          gender: inquiryData?.gender === 'MALE' ? 'male' : 'female',
          first_name: inquiryData?.first_name,
          last_name: inquiryData?.last_name,
          identity_number: String(inquiryData?.identification_number),
          identity_issue_location: inquiryData?.birth_place,
        },
      });
    }
  }, [inquiryData]);

  useEffect(() => {
    setTimeout(() => {
      methods.trigger();
    });
  }, [ownerType]);

  return (
    <Flex direction="column" gap={8}>
      <Box py={24}>
        <PersonalInquiry outSideForm={useFormContext} />
      </Box>
      <RadioGroup
        title="نوع مالکیت"
        name="personal_information.owner_type"
        options={[
          { label: 'حقیقی', value: 'real' },
          { label: 'حقوقی', value: 'legal' },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({
          paddingBottom: '24px',
          gap: '2rem',
        })}
      />
      {ownerType === 'real' ? (
        <InitialRealOwnerForm />
      ) : (
        <InitialLegalOwnerForm />
      )}
      <Box w="100%" mb="1rem">
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
          fullWidth
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Box>
    </Flex>
  );
};
export default InitialOwnerForm;
